import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle, AlertCircle } from 'lucide-react';
import clsx from 'clsx';
import { AppButton } from '@/shared/components/ui/AppButton/AppButton';
import { LoadingSpinner } from '@/shared/components/feedback/LoadingSpinner';
import { queryKeys } from '@/shared/services/query-keys';
import styles from './OAuthCallbackPage.module.scss';

const REDIRECT_DELAY_MS = 3000;
const ACCOUNTS_PATH = '/promote/accounts';

const PLATFORM_DISPLAY_NAMES: Record<string, string> = {
  meta: 'Meta (Facebook & Instagram)',
  google: 'Google Ads',
  twitter: 'X (Twitter)',
};

/**
 * OAuth callback handler page.
 * Receives the authorization result from the platform OAuth flow,
 * shows success/error feedback, and redirects to the accounts page.
 */
export function OAuthCallbackPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const params = useParams({ strict: false }) as { platform?: string };
  const searchParams = useSearch({ strict: false }) as {
    status?: string;
    error?: string;
  };

  const platform = params.platform ?? '';
  const status = searchParams.status ?? '';
  const errorMessage = searchParams.error ?? '';

  const isSuccess = status === 'success';
  const isError = status === 'error';
  const isLoading = !isSuccess && !isError;

  const [countdown, setCountdown] = useState(Math.ceil(REDIRECT_DELAY_MS / 1000));
  const hasInvalidated = useRef(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Clear the auto-redirect timer so a manual nav can't trigger a second one.
  const goToAccounts = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    void navigate({ to: ACCOUNTS_PATH });
  }, [navigate]);

  const platformDisplayName =
    PLATFORM_DISPLAY_NAMES[platform.toLowerCase()] ?? (platform || 'Unknown Platform');

  // Invalidate accounts cache on success
  useEffect(() => {
    if (isSuccess && !hasInvalidated.current) {
      hasInvalidated.current = true;
      void queryClient.invalidateQueries({ queryKey: queryKeys.accounts.all() });
    }
  }, [isSuccess, queryClient]);

  // Auto-redirect countdown
  useEffect(() => {
    if (isLoading) return;

    intervalRef.current = setInterval(() => {
      setCountdown((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => {
      if (intervalRef.current !== null) clearInterval(intervalRef.current);
    };
  }, [isLoading]);

  useEffect(() => {
    if (!isLoading && countdown === 0) goToAccounts();
  }, [isLoading, countdown, goToAccounts]);

  if (isLoading) {
    return (
      <div className={styles.container}>
        <LoadingSpinner size="lg" label="Connecting account..." />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        {isSuccess ? (
          <>
            <div className={clsx(styles.iconWrapper, styles.iconSuccess)}>
              <CheckCircle size={32} />
            </div>
            <h1 className={styles.title}>Account connected successfully!</h1>
            <p className={styles.message}>
              Your <span className={styles.platformName}>{platformDisplayName}</span> account has
              been connected and is ready to use.
            </p>
          </>
        ) : (
          <>
            <div className={clsx(styles.iconWrapper, styles.iconError)}>
              <AlertCircle size={32} />
            </div>
            <h1 className={styles.title}>Connection failed</h1>
            <p className={styles.message}>
              {errorMessage ||
                `Failed to connect your ${platformDisplayName} account. Please try again.`}
            </p>
          </>
        )}

        <p className={styles.redirectText}>
          Redirecting in {countdown} second{countdown !== 1 ? 's' : ''}...
        </p>

        <div className={styles.actions}>
          <AppButton variant="secondary" size="sm" onClick={goToAccounts}>
            Go to Accounts
          </AppButton>
        </div>
      </div>
    </div>
  );
}
